import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, Upload, Brain, GraduationCap, Trophy, CheckCircle2, XCircle } from "lucide-react";

const _isLulusFilled = (v) => {
  const s = (v || "").toString().trim();
  if (!s) return false;
  const low = s.toLowerCase();
  if (["-", "–", "—", "n/a", "na", "tidak", "belum", "0", "."].includes(low)) return false;
  if (/tidak\s*lulus|belum\s*lulus|belum\s*berhasil|gagal/.test(low)) return false;
  return true;
};

const Bars = ({ items, labelKey = "dimensi", color = "#4A6B53" }) => (
  <div className="space-y-1.5">
    {(items || []).map((it, i) => (
      <div key={i}>
        <div className="flex justify-between text-xs mb-0.5"><span className="text-[#1A1C1A] font-medium">{it[labelKey] || it.nama}</span><span className="text-[#5C5F5C]">{it.level || it.skor}</span></div>
        <div className="h-2 rounded-full bg-[#F0ECE4] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, Number(it.skor) || 0)}%`, background: color }} /></div>
      </div>
    ))}
  </div>
);

const Chips = ({ items, cls = "bg-[#F0ECE4] text-[#1A1C1A]" }) => (
  <div className="flex flex-wrap gap-1.5">{(items || []).map((t, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded-full ${cls}`}>{t}</span>)}</div>
);

const List = ({ items }) => (
  <ul className="space-y-1">{(items || []).map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#1A1C1A]"><span className="text-[#4A6B53] mt-0.5">•</span> {t}</li>)}</ul>
);

export function PartnerReport({ report, forParent = false }) {
  if (!report?.data) return null;
  const d = report.data;
  const tp = d.tipe_kepribadian || {};
  return (
    <div className="mb-10" data-testid="partner-report">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-sm font-medium mb-3"><FileCheck2 className="h-4 w-4" /> Dianalisa dari Innate Talent Inventory Talenta</div>
      <div className="clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A] mb-4">
        <div className="flex items-center gap-2 text-white/70 text-sm mb-1"><Brain className="h-4 w-4" /> Tipe Kepribadian (16 Peta Bakat)</div>
        <h3 className="font-heading font-black text-2xl sm:text-3xl">{tp.nama || "-"} {tp.kode ? `(${tp.kode})` : ""}</h3>
        {tp.tagline && <p className="text-[#F4B942] font-medium mt-1">{tp.tagline}</p>}
        {d.narasi_profil && <p className="text-white/85 leading-relaxed mt-3">{d.narasi_profil}</p>}
      </div>
      {d.cita_cita_siswa && (
        <div className="clay-card p-5 mb-4 bg-[#E25E3E]/5 border-[#E25E3E]/25" data-testid="cita-cita-siswa">
          <h4 className="font-heading font-bold text-[#1A1C1A] mb-1 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#E25E3E]" /> Cita-cita Siswa</h4>
          <p className="text-sm text-[#5C5F5C]">{d.cita_cita_siswa}</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {d.potensi_kekuatan?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Potensi & Kekuatan</h4><List items={d.potensi_kekuatan.slice(0, 3)} /></div>}
        {d.kelemahan?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Kelemahan</h4><List items={d.kelemahan} /></div>}
        {d.gaya_belajar && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Gaya Belajar</h4><p className="text-sm text-[#5C5F5C]">{d.gaya_belajar}</p></div>}
        {d.mbti_dimensi?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-3">8 Dimensi MBTI</h4><Bars items={d.mbti_dimensi} color="#E25E3E" /></div>}
        {d.kecerdasan_bawaan?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-3">7 Kecerdasan Bawaan</h4><Bars items={d.kecerdasan_bawaan} labelKey="nama" /></div>}
      </div>
      {d.pekerjaan_sesuai?.length > 0 && (
        <div className="clay-card p-5 mt-4"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Pekerjaan yang Sesuai</h4>
          <div className="space-y-3">{d.pekerjaan_sesuai.map((k, i) => (<div key={i}><p className="text-sm font-semibold text-[#4A6B53] mb-1">{k.kategori}</p><Chips items={(k.daftar || []).slice(0, 5)} cls="bg-[#4A6B53]/10 text-[#4A6B53]" /></div>))}</div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {d.rekomendasi_jurusan_ptn?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2 flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-[#E25E3E]" /> Rekomendasi Jurusan PTN</h4><Chips items={d.rekomendasi_jurusan_ptn} cls="bg-[#E25E3E]/10 text-[#E25E3E]" /></div>}
        {d.rekomendasi_kedinasan?.length > 0 && <div className="clay-card p-5"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Rekomendasi Kedinasan</h4><Chips items={d.rekomendasi_kedinasan} cls="bg-[#1A1C1A]/5 text-[#1A1C1A]" /></div>}
        {forParent && d.saran_orang_tua?.length > 0 && <div className="clay-card p-5 bg-[#F4B942]/10 border-[#F4B942]/30"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Saran bagi Orang Tua</h4><List items={d.saran_orang_tua.slice(0, 4)} /></div>}
        {d.saran_pengembangan_diri?.length > 0 && <div className="clay-card p-5 bg-[#4A6B53]/5 border-[#4A6B53]/25"><h4 className="font-heading font-bold text-[#1A1C1A] mb-2">Saran Pengembangan Diri</h4><List items={d.saran_pengembangan_diri} /></div>}
      </div>
    </div>
  );
}

const SUB = [["PU", "PU"], ["PPU", "PPU"], ["PBM", "PBM"], ["PK", "PK"], ["LBIND", "L.Indo"], ["LBING", "L.Ing"], ["PM", "PM"]];

export function PartnerTryouts({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mb-10" data-testid="partner-tryouts">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-sm font-medium mb-3"><Trophy className="h-4 w-4" /> Hasil TryOut Bimbel Talenta</div>
      <div className="space-y-4">
        {items.map((it) => {
          const h = it.hasil || {};
          const sub = h.subtes || {};
          const hasSub = (Number(h.total_irt) || 0) > 0 || SUB.some(([k]) => (Number(sub[k]?.bnr) || 0) > 0 || (Number(sub[k]?.irt) || 0) > 0);
          return (
            <div key={it.id} className="clay-card p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="font-heading font-bold text-[#1A1C1A]">{it.batch_name}</h4>
                <span className="text-xs text-[#5C5F5C]">{it.uploaded_at ? new Date(it.uploaded_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
              </div>
              {hasSub ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-[#5C5F5C] text-left"><th className="py-1 pr-2">Subtes</th>{SUB.map(([k, l]) => <th key={k} className="py-1 px-2 text-center">{l}</th>)}<th className="py-1 px-2 text-center">Total</th></tr></thead>
                    <tbody>
                      <tr><td className="py-1 pr-2 font-medium">BNR</td>{SUB.map(([k]) => <td key={k} className="py-1 px-2 text-center">{sub[k]?.bnr ?? "-"}</td>)}<td className="py-1 px-2 text-center font-bold">{h.total_bnr ?? "-"}</td></tr>
                      <tr><td className="py-1 pr-2 font-medium">IRT</td>{SUB.map(([k]) => <td key={k} className="py-1 px-2 text-center text-[#E25E3E]">{sub[k]?.irt ?? "-"}</td>)}<td className="py-1 px-2 text-center font-bold text-[#E25E3E]">{h.total_irt ?? "-"}</td></tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-4 text-sm py-1">
                  {h.persen_benar != null && <span className="text-[#5C5F5C]">Skor benar: <b className="text-[#E25E3E] text-lg">{h.persen_benar}%</b></span>}
                  {(Number(h.total_irt) || 0) > 0 && <span className="text-[#5C5F5C]">IRT: <b className="text-[#1A1C1A]">{h.total_irt}</b></span>}
                  <span className="text-xs text-[#9C9A94]">Rincian per subtes tidak tersedia pada dokumen ini.</span>
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-3 text-sm items-center">
                {h.persen_benar != null && <span className="text-[#5C5F5C]">Benar: <b className="text-[#1A1C1A]">{h.persen_benar}%</b></span>}
                {(_isLulusFilled(h.kelulusan) || _isLulusFilled(h.lulus_pilihan)) ? (
                  <>
                    <span data-testid="lulus-status" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Lulus{_isLulusFilled(h.kelulusan) ? ` di ${h.kelulusan}` : ""}</span>
                    {_isLulusFilled(h.lulus_pilihan) && <span className="px-2.5 py-1 rounded-full bg-[#F4B942]/20 text-[#8a6d16] text-xs font-medium">{h.lulus_pilihan}</span>}
                  </>
                ) : (
                  <span data-testid="lulus-status" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Tidak lulus pada salah satu prodi pilihan</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PartnerUploader({ childId, onDone, className = "" }) {
  const [loading, setLoading] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (childId) fd.append("child_id", childId);
    try {
      const { data } = await api.post("/assessment/upload-partner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Laporan mitra terbaca: ${data.nama_terdeteksi || "tersimpan"}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membaca PDF");
    }
    setLoading(false);
  };
  return (
    <label data-testid="partner-upload-label" className={`cursor-pointer px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5 transition-colors inline-flex items-center gap-2 ${className}`}>
      <input type="file" accept="application/pdf" className="hidden" onChange={(e) => up(e.target.files?.[0])} />
      <Upload className="h-4 w-4" /> {loading ? "Membaca PDF…" : "Upload Laporan Asesmen Mitra"}
    </label>
  );
}
